import { convertValueUnit } from '@metservicedev/cf-units';
import type { NcoJSONRAW } from '@metservicedev/ncojs/dist/Model/NcoJSON';
import { DateTime } from 'luxon';

import { getHeaders } from './transformer-utils';

// Please note this cfjson-to-csv mapper works for variables that are only indexed by the time dimension.
interface cfjsonToCsv {
    cfjson: NcoJSONRAW;
    variableUnitMap?: Record<string, string[]>; // varableUnitMap['wind_speed'] = ['m/s', 'km/h']
    timezone?: string;
}

export const cfjsonToCsv = ({ cfjson, variableUnitMap, timezone }: cfjsonToCsv) => {
    const variables = Object.entries(cfjson.variables)
        .filter(([variableKey, variable]) => {
            if (variable.shape.length !== 1) return false;
            if (variable.shape[0] !== 'time') return false;
            return true;
        })
        .map(([variableKey]) => variableKey);

    const variableMappingWithDefaults = variables.reduce<Record<string, string[]>>((prev, variableKey) => {
        if (variableKey === 'time') {
            if (timezone) return { ...prev, [variableKey]: ['UTC', timezone] };
            return { ...prev, [variableKey]: ['UTC'] };
        }
        if (!variableUnitMap) {
            return {
                ...prev,
                [variableKey]: [cfjson.variables[variableKey].attributes?.units],
            };
        }
        return {
            ...prev,
            [variableKey]: variableUnitMap[variableKey] || [cfjson.variables[variableKey].attributes?.units],
        };
    }, {});

    const headers = getHeaders(variableMappingWithDefaults);

    const isSpectra = cfjson.variables.efth?.data?.length > 0;

    if (isSpectra) {
        const efth = cfjson.variables.efth.data;
        const freq = cfjson.variables.freq.data;
        const dir = cfjson.variables.dir.data;
        const time = cfjson.variables.time.data;

        const metaInformation = `description: ${cfjson.attributes?.description} \n modified_at: ${cfjson.attributes?.modified_at} \n ${cfjson.variables.efth?.attributes?.standard_name}, ${cfjson.variables.efth?.attributes?.units}  \n nfreq: ${freq.length}, ${cfjson.variables.freq?.attributes?.units} \n ndir: ${dir.length}, ${cfjson.variables.dir?.attributes?.units} \n ntime: ${time.length}, ${cfjson.variables.time?.attributes?.units} \n\n`;

        const dirHeader = `freq/dir,${dir.join(',')}`;

        const timeSections = time
            .map((t, i) => {
                const rows = freq
                    .map((f, j) => {
                        const values = efth[i][j];
                        return `${f},${values.join(',')}`;
                    })
                    .join('\n');

                return [t, dirHeader, rows].join('\n');
            })
            .join('\n');

        return metaInformation + timeSections;
    }

    const rows = cfjson.variables.time.data
        .map((time: string, timeIndex: number) => {
            return headers
                .map((header) => {
                    const [variableKey, unit] = header.split(':');
                    const variable = cfjson.variables[variableKey];

                    if (variableKey === 'time') {
                        if (unit === timezone) return DateTime.fromISO(time).setZone(timezone).toISO();

                        return DateTime.fromISO(time).setZone('UTC').toISO();
                    }

                    const value = variable.data[timeIndex];
                    if (typeof value === 'string') return value;

                    const usefulUnit = variable.attributes?.units === 'degC' ? 'tempC' : variable.attributes?.units;
                    // if the specified unit is the default unit of the data, just return the data, no conversion needed.
                    if (unit === usefulUnit) return value;
                    if (!value) return null;
                    try {
                        const convertedValue = convertValueUnit({
                            from: usefulUnit,
                            to: unit,
                            value: value,
                        });
                        return convertedValue;
                    } catch (e) {
                        console.error(e);
                        console.log('failed to convert a unit when convert to csv');
                        throw e;
                    }
                })
                .join(',');
        })
        .join('\n');

    return [headers.join(','), rows].join('\n');
};
